/* eslint react/forbid-prop-types: 0 */
import React, {
  useContext, useState, memo, useEffect, useMemo, useRef
} from 'react';
import PropTypes from 'prop-types';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import {
  ProductPod,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductServiceAddOns,
  ProductLink,
  ProductIdentifier, ProductBadge,
  ProductImage, PodSection, ProductCompare,
  ProductCustomOptions, PodSpacer
} from '@thd-olt-component-react/product-pod';
import { AddToQuote } from '@thd-olt-component-react/add-to-quote';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { Price } from '@thd-olt-component-react/price';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Quantity } from '@thd-olt-component-react/quantity';
import { NonBuyableQuoteCenter } from '@thd-olt-component-react/non-buyable-quote-center';
import { IconButton, Menu, MenuItem } from '@one-thd/sui-atomic-components';
import { More } from '@one-thd/sui-icons';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ProductListContext } from '../ProductListContext';
import { ProductNote, NoteButton } from '../note';
import { DeleteButton } from '../delete-button/DeleteButton';
import { isItemEqual, debounce, isGeneralMerchProduct } from '../productListHelper';
import { ListViewPlaceHolder } from './ListViewPlaceHolder';

export const ListPodMobile = memo(({
  handleQuantityUpdate,
  itemId,
  podIndex,
  quantity: quantityProp,
  showStoreSku,
  storeId,
  searchKeyword
}) => {

  const {
    productsDisplayed,
    isSharedList
  } = useContext(ProductListContext);
  const { isB2BCustomer = false } = useThdCustomer();
  const [quantity, setQuantity] = useState(quantityProp);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const noteButtonRef = useRef(null);

  const product = productsDisplayed.find((item) => item.itemId === itemId);

  // Commented out to prevent individual product API calls
  // Rely entirely on batch loading from observer system
  // const { loading } = useDataModel('product', {
  //   variables: {
  //     itemId,
  //   },
  //   skip: !itemId || Boolean(product)
  // });

  const updateListQuantity = useMemo(() => {
    return debounce(handleQuantityUpdate, 1000);
  }, []);

  useEffect(() => {
    if (quantity !== quantityProp) {
      setQuantity(quantityProp);
    }
  }, [quantityProp]);

  function isNumeric(value) {
    return /^-?\d+$/.test(value);
  }

  const onQuantityUpdate = (newQuantity) => {
    if (isNumeric(newQuantity?.quantity) && newQuantity?.quantity !== quantity) {
      setQuantity(newQuantity?.quantity);
      updateListQuantity({ itemId, quantity: quantityProp, newQuantity, price: product?.pricing?.value });
    }
  };
  const noATCFulfillment = isGeneralMerchProduct(product);

  // const handleOpenMenu = (event) => {
  //   setAnchorEl(event.currentTarget);
  //   setOpenMenu(true);
  // };

  // const handleCloseMenu = () => {
  //   setAnchorEl(null);
  //   setOpenMenu(false);
  // };

  // const handleOpenNoteDrawer = () => {
  //   handleCloseMenu();
  //   // Trigger NoteButton's drawer open
  //   if (noteButtonRef.current) {
  //     noteButtonRef.current.click();
  //   }
  // };

  // Show placeholder until product data is loaded via batch observer system
  if (!product) {
    return (
      <ListViewPlaceHolder count={1} isMobile />
    );
  }

  return (
    <ProductPod
      itemId={itemId}
      storeId={storeId}
      padding="sui-p-2 sui-pb-0 sui-pl-0"
      variables={{ skip: true }}
      analyticsData={{
        parent: 'product-list', position: podIndex
      }}
      render={(pod) => (
        <PodSection gap="sui-gap-1" padding="sui-p-0" columnAlign alignTop noPadding>
          <div
            className="sui-flex sui-justify-between"
          />
          <div className="sui-flex sui-my-1">
            <ProductBadge itemId={pod.itemId} storeId={storeId} />
          </div>
          <PodSpacer padding={['TOP', 'BOTTOM']}>
            <ProductHeader
              brand="inline"
              itemId={pod.itemId}
              brandTitleMaxLine={4}
              disableShopThisCollection
            />
          </PodSpacer>
          <div className="sui-grid sui-grid-cols-2">
            <PodSpacer padding={['ALL']}>
              <PodSpacer padding={['TOP', 'BOTTOM']}>
                <ProductImage itemId={pod.itemId} />
              </PodSpacer>
              <SuperSkuMiniSwatches
                itemId={pod.itemId}
                storeId={storeId}
                onChange={pod.onChange}
                onHover={pod.onSSKUHover}
              />
              {
                searchKeyword && (
                  <PodSpacer padding={['TOP']}>
                    <div className="sui-text-thd-blue sui-text-xs sui-font-normal sui-py-2">
                      Searched for:
                      {' '}
                      <span className="sui-font-bold">{`"${searchKeyword}"`}</span>
                    </div>
                  </PodSpacer>
                )
              }
            </PodSpacer>
            <PodSpacer padding={['ALL']}>
              <PodSpacer>
                <ProductIdentifier itemId={pod.itemId} type="itemId" />
                <ProductIdentifier itemId={pod.itemId} type="model" />
                {showStoreSku && <ProductIdentifier itemId={pod.itemId} type="storeSku" />}
              </PodSpacer>
              <ProductRating itemId={pod.itemId} />
              <PodSpacer minHeight="60px">
                <Price
                  itemId={pod.itemId}
                  large={false}
                  storeId={storeId}
                  displayEachUom={false}
                  showPreferredPricingBadge
                  stackWasPrice
                />
              </PodSpacer>
              <div>
                {
                  isSharedList ? `Qty: ${quantity}` : (
                    <Quantity
                      onChange={onQuantityUpdate}
                      value={quantity}
                    />
                  )
                }
              </div>
              <PodSpacer minHeight="90px" padding={['TOP', 'BOTTOM']}>
                <FulfillmentPodStore itemId={pod.itemId} storeId={pod.storeId} />
                <FulfillmentPodShipping itemId={pod.itemId} storeId={pod.storeId} />
              </PodSpacer>
            </PodSpacer>
          </div>
          <PodSpacer padding={['TOP', 'BOTTOM']}>
            <ProductATC
              itemId={pod.itemId}
              checkGeneric
              quantity={quantity}
              noATCFulfillment={noATCFulfillment}
            />
          </PodSpacer>
          {
            isB2BCustomer && (
              <PodSpacer minHeight="40px" padding={['BOTTOM']}>
                <AddToQuote
                  itemId={pod.itemId}
                  quantity={quantity}
                />
              </PodSpacer>
            )
          }
          <ProductNote itemId={itemId} />
        </PodSection>
      )}
    />
  );
}, isItemEqual);

ListPodMobile.displayName = 'ListPodMobile';

ListPodMobile.propTypes = {
  handleQuantityUpdate: PropTypes.func,
  itemId: PropTypes.string.isRequired,
  podIndex: PropTypes.number,
  quantity: PropTypes.number,
  showStoreSku: PropTypes.bool,
  storeId: PropTypes.string,
  searchKeyword: PropTypes.string,
};

ListPodMobile.defaultProps = {
  handleQuantityUpdate: () => {},
  podIndex: 1,
  quantity: 1,
  showStoreSku: true,
  storeId: '',
  searchKeyword: ''
};

ListPodMobile.dataModel = extend(
  ProductPod,
  ProductATC,
  ProductImage,
  ProductLink,
  ProductServiceAddOns,
  Price,
  SuperSkuMiniSwatches,
  ProductRating,
  ProductCompare,
  ProductSample,
  ProductIdentifier,
  ProductHeader,
  ProductBadge,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  ProductCustomOptions,
  NonBuyableQuoteCenter,
  AddToQuote
);
